import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cloneDeep, set as lodashSet } from "lodash";
import Layout, { Container } from "../components/layout";
import { useTheme } from "../config/themeContext";
import { InfiLoader } from "../components/loader";

const Appearance = () => {
  const { theme, saveTheme, loading, error } = useTheme();
  const [localTheme, setLocalTheme] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const updateLiveTheme = (updatedTheme) => {
    Object.entries(updatedTheme).forEach(([section, settings]) => {
      Object.entries(settings).forEach(([key, value]) => {
        document.documentElement.style.setProperty(
          `--${section}-${key}`,
          value
        );
      });
    });
  };

  const handleChange = (path, value) => {
    const updatedTheme = cloneDeep(localTheme);
    lodashSet(updatedTheme, path, value); // Update nested property
    setLocalTheme(updatedTheme);
    setIsChanged(true);

    // Apply live changes
    updateLiveTheme(updatedTheme);
  };

  const saveChanges = async () => {
    await saveTheme(localTheme); // Save to API
    setIsChanged(false);
    alert("Theme updated successfully!");
  };

  const handleDiscard = () => {
    setLocalTheme(theme); // Revert local changes
    updateLiveTheme(theme); // Revert live changes
    setIsChanged(false);
    setIsModalOpen(false);
    navigate(-1);
  };

  useEffect(() => {
    setLocalTheme(theme); // Initialize local theme with the current theme
    updateLiveTheme(theme); // Apply the current theme live
  }, [theme]);

  if (loading) return <InfiLoader maintext="Theme Loading..." />;
  if (error) return <div>{error}</div>;

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="container mx-auto p-4">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-center mb-6">
            Appearance Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <ColorPicker
              label="Sidebar Background"
              value={localTheme?.sideBar?.bgColour || "#000000"}
              onChange={(value) => handleChange("sideBar.bgColour", value)}
            />
            <ColorPicker
              label="Sidebar Text"
              value={localTheme?.sideBar?.textColour || "#ffffff"}
              onChange={(value) => handleChange("sideBar.textColour", value)}
            />

            <ColorPicker
              label="Active Nav Background"
              value={localTheme?.sideBar?.activeNavBgColour || "#ffffff"}
              onChange={(value) => handleChange("sideBar.activeNavBgColour", value)}
            />
            <ColorPicker
              label="Active Nav Text Color"
              value={localTheme?.sideBar?.activeNavTextColour || "#ffffff"}
              onChange={(value) => handleChange("sideBar.activeNavTextColour", value)}
            />
          </div>
          <div className="mt-6 text-center">
            <button
              onClick={saveChanges}
              className={`bg-blue-600 text-white py-2 px-6 rounded-md mr-4 ${
                !isChanged && "opacity-50 cursor-not-allowed"
              }`}
              disabled={!isChanged}
            >
              Save Changes
            </button>
            <button
              onClick={handleDiscard}
              className="bg-gray-300 text-gray-800 py-2 px-6 rounded-md"
            >
              Discard
            </button>
          </div>
        </div>
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={saveChanges}
          onDiscard={handleDiscard}
        />
      </Container>
    </Layout>
  );
};

export default Appearance;

const ColorPicker = ({ label, value, onChange }) => {
  const [color, setColor] = useState(value);

  const handleInputChange = (e) => {
    const hex = e.target.value;
    if (/^#([0-9A-F]{3}){1,2}$/i.test(hex)) {
      setColor(hex);
      onChange(hex);
    }
  };

  const handlePickerChange = (e) => {
    const hex = e.target.value;
    setColor(hex);
    onChange(hex);
  };

  useEffect(() => {
    setColor(value);
  }, [value]);

  return (
    <div className="p-4 bg-gradient-to-r from-gray-500 to-purple-500 rounded-lg shadow-lg">
      <label className="block text-lg font-semibold text-white mb-4">
        {label}
      </label>
      <div className="flex items-center justify-center flex-col gap-4">
        {/* Color Picker */}
        <div
          className="relative w-full h-20 rounded-lg overflow-hidden border-2 border-white shadow-md"
          style={{ backgroundColor: color }}
        >
          <input
            type="color"
            value={color}
            onChange={handlePickerChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Hex Input */}
        <input
          type="text"
          value={color}
          onChange={handleInputChange}
          placeholder="#000000"
          className="w-full px-4 py-2 text-center text-lg font-medium text-gray-700 rounded-lg bg-white border-2 border-gray-300 shadow-md outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500 hover:scale-105 transition-transform duration-300"
        />
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onSave, onDiscard }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded shadow-lg w-1/3">
        <h3 className="text-xl font-bold mb-4">Unsaved Changes</h3>
        <p className="mb-6">
          You have unsaved changes. Do you want to save or discard them?
        </p>
        <div className="flex justify-end">
          <button
            onClick={onDiscard}
            className="bg-gray-300 text-gray-800 py-2 px-4 rounded mr-2"
          >
            Discard
          </button>
          <button
            onClick={onSave}
            className="bg-blue-600 text-white py-2 px-4 rounded"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};
